import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const API = "/api/auditoria/documento";

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

const MESES = [
  "",
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const nombrePersona = (persona) =>
  [
    persona?.nombre1Instructor,
    persona?.nombre2Instructor,
    persona?.apellido1Instructor,
    persona?.apellido2Instructor,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim() || "—";

const nombrePeriodo = (mes, anio) => {
  const numeroMes = Number(mes);
  const nombreMes = MESES[numeroMes] || "";

  if (!mes && !anio) return "—";
  if (!mes) return String(anio || "—");
  if (!anio) return nombreMes || String(mes);

  return `${nombreMes || mes} ${anio}`;
};

const formatearFecha = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime())
    ? "—"
    : fecha.toLocaleString("es-CO");
};

const claseEstado = (estado) => {
  const mapa = {
    "Sin revisar": "badge bg-secondary",
    "En revisión": "badge bg-info text-dark",
    "Requiere ajustes": "badge bg-warning text-dark",
    Aprobado: "badge bg-success",
    Rechazado: "badge bg-danger",
  };

  return mapa[estado] || "badge bg-secondary";
};

const Auditoria_ListarDocumento = () => {
  const navigate = useNavigate();

  const [documentos, setDocumentos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [instructores, setInstructores] = useState([]);

  const [codigoTipoDocumento, setCodigoTipoDocumento] = useState("");
  const [instructorObjectId, setInstructorObjectId] = useState("");
  const [estado, setEstado] = useState("");
  const [periodoMes, setPeriodoMes] = useState("");
  const [periodoAnio, setPeriodoAnio] = useState("");
  const [buscar, setBuscar] = useState("");

  const [loading, setLoading] = useState(false);
  const [procesandoId, setProcesandoId] = useState("");

  const manejarError = useCallback(
    (error, mensaje) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        Swal.fire(
          "Sesión expirada",
          "Inicia sesión nuevamente.",
          "warning"
        ).then(() => navigate("/login"));
        return;
      }

      Swal.fire(
        "Error",
        error?.response?.data?.message || mensaje || "Ocurrió un error.",
        "error"
      );
    },
    [navigate]
  );

  const cargarTipos = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/tipos-soportados`,
        authConfig()
      );

      const lista = Array.isArray(data?.tipos) ? data.tipos : [];
      setTipos(lista);

      if (!codigoTipoDocumento && lista.length > 0) {
        setCodigoTipoDocumento(lista[0].codigoTipoDocumento || "");
      }
    } catch (error) {
      manejarError(error, "No se pudieron cargar los tipos documentales.");
    }
  }, [codigoTipoDocumento, manejarError]);

  const cargarDocumentos = useCallback(async () => {
    if (!codigoTipoDocumento) return;

    try {
      setLoading(true);

      const params = {
        codigoTipoDocumento,
      };

      if (instructorObjectId) params.instructorObjectId = instructorObjectId;
      if (estado) params.estado = estado;
      if (periodoMes) params.periodoMes = periodoMes;
      if (periodoAnio) params.periodoAnio = periodoAnio;
      if (buscar.trim()) params.buscar = buscar.trim();

      const { data } = await axios.get(`${API}/disponibles`, {
        ...authConfig(),
        params,
      });

      setDocumentos(Array.isArray(data?.documentos) ? data.documentos : []);
      setInstructores(Array.isArray(data?.instructores) ? data.instructores : []);
    } catch (error) {
      manejarError(error, "No se pudieron cargar los documentos auditables.");
      setDocumentos([]);
    } finally {
      setLoading(false);
    }
  }, [
    codigoTipoDocumento,
    instructorObjectId,
    estado,
    periodoMes,
    periodoAnio,
    buscar,
    manejarError,
  ]);

  useEffect(() => {
    cargarTipos();
  }, [cargarTipos]);

  useEffect(() => {
    if (codigoTipoDocumento) {
      cargarDocumentos();
    }
  }, [codigoTipoDocumento, cargarDocumentos]);

  const iniciarRevision = useCallback(
    async (item) => {
      try {
        setProcesandoId(String(item.documentoObjectId));

        const { data } = await axios.post(
          `${API}/iniciar-revision`,
          {
            codigoTipoDocumento: item.codigoTipoDocumento,
            documentoObjectId: item.documentoObjectId,
          },
          authConfig()
        );

        const expedienteId = data?.auditoriaDocumento?._id;

        if (!expedienteId) {
          throw new Error("No fue posible obtener el expediente de auditoría.");
        }

        navigate(`/auditoria/documento/revisar/${expedienteId}`);
      } catch (error) {
        manejarError(error, "No se pudo iniciar la revisión.");
      } finally {
        setProcesandoId("");
      }
    },
    [navigate, manejarError]
  );

  const abrirDocumento = useCallback(
    async (item) => {
      if (item.estadoAuditoria === "Sin revisar") {
        return iniciarRevision(item);
      }

      if (item.estadoAuditoria === "Requiere ajustes") {
        return iniciarRevision(item);
      }

      if (item.auditoriaDocumentoObjectId) {
        navigate(
          `/auditoria/documento/revisar/${item.auditoriaDocumentoObjectId}`
        );
      }
    },
    [iniciarRevision, navigate]
  );

  const totalPorEstado = useMemo(() => {
    const conteo = {
      "Sin revisar": 0,
      "En revisión": 0,
      "Requiere ajustes": 0,
      Aprobado: 0,
      Rechazado: 0,
    };

    documentos.forEach((item) => {
      if (conteo[item.estadoAuditoria] !== undefined) {
        conteo[item.estadoAuditoria] += 1;
      }
    });

    return conteo;
  }, [documentos]);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <h4 className="mb-1">Panel documental de auditoría</h4>
          <p className="text-muted mb-0">
            Consulta directa de los documentos de los instructores asignados.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={cargarDocumentos}
          disabled={loading || !codigoTipoDocumento}
        >
          <i className="fa-solid fa-rotate me-2"></i>
          Actualizar
        </button>
      </div>

      <div className="row g-3 mb-3">
        {Object.entries(totalPorEstado).map(([nombre, total]) => (
          <div className="col-6 col-md" key={nombre}>
            <div className="card h-100 shadow-sm">
              <div className="card-body py-3">
                <div className="small text-muted">{nombre}</div>
                <div className="fs-4 fw-semibold">{total}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Tipo documental</label>
              <select
                className="form-select"
                value={codigoTipoDocumento}
                onChange={(e) => setCodigoTipoDocumento(e.target.value)}
              >
                {tipos.length === 0 && (
                  <option value="">Sin cargadores configurados</option>
                )}
                {tipos.map((tipo) => (
                  <option key={tipo._id} value={tipo.codigoTipoDocumento}>
                    {tipo.nombreTipoDocumento}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Instructor</label>
              <select
                className="form-select"
                value={instructorObjectId}
                onChange={(e) => setInstructorObjectId(e.target.value)}
              >
                <option value="">Todos mis instructores</option>
                {instructores.map((instructor) => (
                  <option key={instructor._id} value={instructor._id}>
                    {nombrePersona(instructor)} - {instructor.documentoInstructor}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Estado</label>
              <select
                className="form-select"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="Sin revisar">Sin revisar</option>
                <option value="En revisión">En revisión</option>
                <option value="Requiere ajustes">Requiere ajustes</option>
                <option value="Aprobado">Aprobado</option>
                <option value="Rechazado">Rechazado</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Mes</label>
              <select
                className="form-select"
                value={periodoMes}
                onChange={(e) => setPeriodoMes(e.target.value)}
              >
                <option value="">Todos</option>
                {MESES.slice(1).map((mes, index) => (
                  <option key={mes} value={index + 1}>
                    {mes}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Año</label>
              <input
                type="number"
                className="form-control"
                value={periodoAnio}
                onChange={(e) => setPeriodoAnio(e.target.value)}
                placeholder="2026"
              />
            </div>

            <div className="col-md-10">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                className="form-control"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                placeholder="Instructor, documento, periodo o estado..."
              />
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-dark w-100"
                onClick={cargarDocumentos}
                disabled={loading || !codigoTipoDocumento}
              >
                <i className="fa-solid fa-magnifying-glass me-2"></i>
                Consultar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Instructor</th>
                  <th>Documento</th>
                  <th>Periodo</th>
                  <th>Última modificación</th>
                  <th>Revisiones</th>
                  <th>Estado auditoría</th>
                  <th className="text-end">Acción</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <div className="spinner-border spinner-border-sm me-2" />
                      Consultando documentos...
                    </td>
                  </tr>
                ) : documentos.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      No se encontraron documentos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  documentos.map((item) => {
                    const procesando =
                      procesandoId === String(item.documentoObjectId);

                    const esFinal = ["Aprobado", "Rechazado"].includes(
                      item.estadoAuditoria
                    );

                    return (
                      <tr
                        key={`${item.documentoModel}-${item.documentoObjectId}`}
                      >
                        <td>
                          <strong>{nombrePersona(item.instructorObjectId)}</strong>
                          <div className="small text-muted">
                            {item.instructorObjectId?.documentoInstructor || "—"}
                          </div>
                        </td>

                        <td>
                          <strong>
                            {item.tipoDocumentoObjectId?.nombreTipoDocumento ||
                              item.codigoTipoDocumento}
                          </strong>
                        </td>

                        <td>{nombrePeriodo(item.periodoMes, item.periodoAnio)}</td>

                        <td>{formatearFecha(item.documentoUpdatedAt)}</td>

                        <td>{item.numeroRevisionActual ?? 0}</td>

                        <td>
                          <span className={claseEstado(item.estadoAuditoria)}>
                            {item.estadoAuditoria}
                          </span>
                        </td>

                        <td className="text-end">
                          <button
                            type="button"
                            className={
                              esFinal
                                ? "btn btn-sm btn-outline-secondary"
                                : "btn btn-sm btn-primary"
                            }
                            disabled={procesando}
                            onClick={() => abrirDocumento(item)}
                          >
                            {procesando ? (
                              <>
                                <i className="fa-solid fa-spinner fa-spin me-2"></i>
                                Abriendo...
                              </>
                            ) : esFinal ? (
                              <>
                                <i className="fa-solid fa-eye me-2"></i>
                                Ver revisión
                              </>
                            ) : item.estadoAuditoria === "En revisión" ? (
                              <>
                                <i className="fa-solid fa-magnifying-glass me-2"></i>
                                Continuar
                              </>
                            ) : item.estadoAuditoria === "Requiere ajustes" ? (
                              <>
                                <i className="fa-solid fa-rotate me-2"></i>
                                Revisar nuevamente
                              </>
                            ) : (
                              <>
                                <i className="fa-solid fa-magnifying-glass me-2"></i>
                                Revisar
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auditoria_ListarDocumento;
